import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { useToastManager } from "@porsche-design-system/components-react"
import { getRoofColorOptionsRoute, setSelectedRoofColorRoute } from "@slfinrtl/ui-bff-routes"
import { useEffect, useRef, useState } from "react"
import { useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { useBffDynamic } from "src/hooks"
import type { SelectCatalogRoofColorContainerProps } from "./SelectCatalogRoofColorContainer"
import type { SelectCatalogRoofColorProps, SelectCatalogRoofColorSelectOption } from "./view/SelectCatalogRoofColor"

export const useSelectCatalogRoofColor = ({
	currentRoofColor,
	inventoryId,
	onRoofColorSelected,
	orderType,
	showCustomRoofColorContainer,
}: SelectCatalogRoofColorContainerProps): SelectCatalogRoofColorProps | undefined => {
	const { formatMessage } = useIntl()
	const { locale } = useDealerContext()

	const { addMessage } = useToastManager()
	const addMessageRef = useRef(addMessage)

	const [selectedOptionCode, setSelectedOptionCode] = useState<string | undefined>(
		currentRoofColor?.optionCode ?? undefined,
	)
	const [allOptions, setAllOptions] = useState<SelectCatalogRoofColorSelectOption[]>([])
	const [isLoading, setIsLoading] = useState<boolean>(true)
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

	const getRoofColorOptions = useBffDynamic(getRoofColorOptionsRoute, "inline")
	const submitSelectedRoofColor = useBffDynamic(setSelectedRoofColorRoute, "inline")

	useEffect(() => {
		if (orderType.code) {
			getRoofColorOptions({
				body: undefined,
				pathParams: {},
				queryParams: {
					"model-year": orderType.modelYear.toString(),
					"order-type-code": orderType.code,
				},
			})
				.then(setAllOptions)
				.finally(() => setIsLoading(false))
		} else {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setAllOptions([])
			setIsLoading(false)
		}
	}, [getRoofColorOptions, orderType.code, orderType.modelYear]) // TODO: why do tests fail with just orderType?

	const onSubmit = async () => {
		if (selectedOptionCode) {
			setIsSubmitting(true)

			try {
				await submitSelectedRoofColor({
					body: {
						optionCode: selectedOptionCode,
					},
					pathParams: {},
					queryParams: { "inventory-id": inventoryId },
				})
			} catch {
				addMessageRef.current({
					state: "info",
					text: formatMessage(formMessages.errorGeneral),
				})
				return
			} finally {
				setIsSubmitting(false)
			}

			const selectedOption = allOptions.find((option) => option.optionCode === selectedOptionCode)
			if (selectedOption) {
				onRoofColorSelected({
					hexCode: selectedOption.hexCode,
					name: {
						[locale]: selectedOption.name,
					},
					optionCode: selectedOption.optionCode,
					tagKey: undefined,
				})
			}
		}
	}

	return !isLoading
		? {
				isSubmitting,
				onSubmit,
				roofColor: {
					options: allOptions,
					setValue: setSelectedOptionCode,
					value: selectedOptionCode,
				},
				showCustomRoofColorContainer,
			}
		: undefined
}
